// Book media from a listing + the "what's missing" prompt.
//
// GET /listing-media tells a listing page what media it has, what's missing
// (rules: backend/common/listing_media.py) and whether a shoot is already
// booked. "Book media" asks the API for a 30-minute signed handoff link
// (ids only) and opens www /book on the package step with this listing's
// address, details and the agent's own contact filled in. If the handoff
// can't be minted it falls back to the plain prefill link (contact details in
// the query string, the earlier pattern) so booking never dead-ends.
import { useCallback, useEffect, useState } from "react";
import api from "./api";
import { bookShootHref } from "./navConfig";

export const GAP_FOCUSES = ["photos", "visit", "video", "floorplan", "drone", "staging"];

export function useListingMedia({ dealId, propertyId } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!!(dealId || propertyId));
  const load = useCallback(() => {
    if (!dealId && !propertyId) return;
    setLoading(true);
    api.get("/listing-media", { params: propertyId ? { property_id: propertyId } : { deal_id: dealId } })
      .then((r) => setData(r.data && typeof r.data === "object" && !Array.isArray(r.data) ? r.data : null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [dealId, propertyId]);
  useEffect(() => { load(); }, [load]);
  return { data, loading, reload: load };
}

/** Service the listing's "Book media" preselects: the first thing missing (the prompt's headline). */
export function primaryGapFocus(summary) {
  const first = (Array.isArray(summary?.gaps) ? summary.gaps : []).find((g) => GAP_FOCUSES.includes(g?.id));
  return first ? first.id : null;
}

/** Plain-param fallback: listing details + focus are not personal; contact follows the earlier pattern. */
export const PHOTO_REASONS = ["none", "few", "phone", "low_res", "defects"];

export function fallbackBookHref({ address, user, details, focus, gaps, photoReason } = {}) {
  const base = bookShootHref({ address, user });
  const q = new URLSearchParams(base.includes("?") ? base.split("?")[1] : "");
  const d = details || {};
  const put = (k, v) => { if (v !== null && v !== undefined && v !== "") q.set(k, String(v)); };
  put("type", d.property_type);
  put("sqft", d.sqft);
  put("beds", d.bedrooms);
  put("baths", d.bathrooms);
  put("price", d.list_price);
  put("lot", d.lot_acres);
  if (d.vacant === true) put("vacant", "1");
  if (GAP_FOCUSES.includes(focus)) put("focus", focus);
  const g = (gaps || []).filter((x) => GAP_FOCUSES.includes(x));
  if (g.length) put("gaps", g.join(","));
  if (g.includes("photos") && PHOTO_REASONS.includes(photoReason)) put("photo", photoReason);
  if (q.get("name") && q.get("email") && q.get("address")) put("step", "packages");
  const qs = q.toString();
  return `${base.split("?")[0]}${qs ? `?${qs}` : ""}`;
}

/** Mint the signed handoff and leave for www /book. Resolves only on failure paths. */
export async function openBookMedia({ dealId, propertyId, focus, fallback } = {}) {
  try {
    const body = { focus: GAP_FOCUSES.includes(focus) ? focus : null };
    if (propertyId) body.property_id = propertyId; else body.deal_id = dealId;
    const { data } = await api.post("/book/handoff", body);
    if (data && typeof data.url === "string" && /^https:\/\/(www\.)?brxs\.ca\/book\?/.test(data.url)) {
      window.location.assign(data.url);
      return;
    }
    throw new Error("no handoff url");
  } catch {
    window.location.assign(fallback || bookShootHref({}));
  }
}

// Dismissal is per listing, per signed-in user, in this browser (no server
// preference store exists yet). A gap that wasn't there when dismissed brings
// the prompt back; the same gaps stay quiet.
const KEY = (uid, kind, id) => `bx:media-prompt:${uid || "anon"}:${kind}:${id}`;

export function dismissedGaps(uid, kind, id) {
  try {
    const raw = window.localStorage.getItem(KEY(uid, kind, id));
    const v = raw ? JSON.parse(raw) : null;
    return Array.isArray(v?.gaps) ? v.gaps : [];
  } catch { return []; }
}

export function dismissGaps(uid, kind, id, gaps) {
  try {
    window.localStorage.setItem(KEY(uid, kind, id), JSON.stringify({ gaps: gaps || [], at: Date.now() }));
  } catch { /* private mode: dismiss for this view only */ }
}

export function visibleGaps(gaps, dismissed) {
  const list = Array.isArray(gaps) ? gaps : [];
  const off = new Set(dismissed || []);
  return list.some((g) => !off.has(g.id)) ? list : [];
}
